import AppPagination from "@app/common/components/AppPagination";
import { formattedDataSelect } from "@app/common/utils/utils";
import { GCidAccount } from "@app/config/globalsConst";
import routes from "@app/config/routes";
import { useGetDoctorList, useGetSiteList } from "@app/services/common/hooks";
import { useGetOrdersList } from "@app/services/drugDelivery/hooks";
import { Medicine, OrderListFilters } from "@app/services/drugDelivery/types";
import { useDeliveryData } from "@app/store/useDeliverySearchData";
import { Box, Card, Flex, Grid, Select, Table, TextInput, Title } from "@mantine/core";
import { useDebouncedState } from "@mantine/hooks";
import { IconChevronRight } from "@tabler/icons-react";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Fragment } from "react/jsx-runtime";

interface Props {
  type: "corpClient" | "patient";
  corporateClient: number | string;
}

const medicalOrderTypeLabels: Record<string, string> = {
  insurance: "Aseguradora",
  particular: "Particular",
  sugess: "Sugerencia"
};

const InsuranceList = ({ type, corporateClient }: Props) => {
  const setShowDelivery = useDeliveryData((store) => store.setDataDispense);

  const navigate = useNavigate();

  const [filters, setFilters] = useDebouncedState<OrderListFilters>(
    {
      type,
      endDate: "",
      startDate: "",
      patient: "",
      doctor: "",
      site: "",
      corporateClient,
      page: 1
    },
    500
  );

  const {
    data: ordersList = {
      results: [],
      rowTotal: 0
    }
  } = useGetOrdersList({
    ...filters,
    deliveryStatus: type === "corpClient" ? "available" : undefined,
    perpage: 10
  });

  const {
    data: doctorList = {
      results: [],
      rowTotal: 0
    }
  } = useGetDoctorList({
    eaccount: GCidAccount()
  });

  const {
    data: sitesList = {
      results: [],
      rowTotal: 0
    }
  } = useGetSiteList({
    eaccount: GCidAccount(),
    modality: "on_site",
    status: "enabled"
  });

  useEffect(() => {
    setFilters((state) => ({ ...state, type, corporateClient }));
  }, [type, corporateClient, setFilters]);

  const handleCard = (option: (typeof ordersList.results)[number]) => {
    const { medicalOrderType, orderId, rcdInfo } = option;
    const fullDeliveryIsApproved = rcdInfo?.fullDelivery;
    const isPendingCompleteDelivery = ["Pendiente", "Aprobado"].includes(rcdInfo?.status || "");

    setShowDelivery({
      orderId: Number(orderId),
      fullDeliveryIsApproved,
      isPendingCompleteDelivery,
      ...(medicalOrderType === "insurance" && { showDeliveryBy: true }),
      ...((["particular", "sugess"] as string[]).includes(medicalOrderType) && {
        deliveryBy: "freeSaleWithOrder"
      })
    });

    if (medicalOrderType !== "insurance") {
      navigate(routes.getDeliveryDispensation);
    }
  };

  const FiltersHeader = () => {
    return (
      <Grid align="flex-end" mb="lg" w="100%">
        <Grid.Col span={3}>
          <TextInput
            data-testid="startDate"
            label="Fecha desde"
            type="date"
            placeholder="Escribir..."
            onChange={({ target }) => {
              setFilters((state) => ({
                ...state,
                startDate: target.value
              }));
            }}
          />
        </Grid.Col>
        <Grid.Col span={3}>
          <TextInput
            data-testid="endDate"
            label="Fecha hasta"
            type="date"
            placeholder="Escribir..."
            onChange={({ target }) => {
              setFilters((state) => ({
                ...state,
                endDate: target.value
              }));
            }}
          />
        </Grid.Col>
        <Grid.Col span={3}>
          <Select
            data-testid="site"
            label="Sede"
            data={formattedDataSelect(sitesList.results, "site", "siteId")}
            searchable
            placeholder="Seleccionar..."
            onChange={(value) => {
              setFilters((state) => ({
                ...state,
                site: value ?? ""
              }));
            }}
          />
        </Grid.Col>
        <Grid.Col span={3}>
          <Select
            data-testid="doctor"
            label="Médico"
            searchable
            data={formattedDataSelect(doctorList.results, "mdName", "mdId")}
            placeholder="Seleccionar..."
            onChange={(value) => {
              setFilters((state) => ({
                ...state,
                doctor: value ?? ""
              }));
            }}
          />
        </Grid.Col>
      </Grid>
    );
  };

  const renderMedicinesSummary = (medicines: Medicine[], idx: number) => {
    if (!medicines?.length) {
      return null;
    }
    return (
      <Box py={"sm"} data-testid={`insurancelist-medicines-container-${idx}`}>
        <Table
          className="biowel-table"
          withTableBorder
          verticalSpacing={"0.3rem"}
          data-testid={`insurancelist-medicines-table-${idx}`}
        >
          <Table.Thead>
            <Table.Tr>
              <Table.Th ta={"start"}>Código</Table.Th>
              <Table.Th>Nombre</Table.Th>
              <Table.Th ta={"center"}>Cantidad ordenada</Table.Th>
              <Table.Th ta={"center"}>Cantidad entregada</Table.Th>
              <Table.Th ta={"center"}>Cantidad pendiente</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {medicines.map((medicine, medicineIdx) => (
              <Table.Tr
                key={medicineIdx}
                data-testid={`insurancelist-medicine-row-${idx}-${medicineIdx}`}
              >
                <Table.Td>{medicine?.code ?? "-"}</Table.Td>
                <Table.Td>{medicine?.name ?? "-"}</Table.Td>
                <Table.Td ta={"center"}>{medicine?.qtyOrder ?? "-"} unds.</Table.Td>
                <Table.Td ta={"center"}>{medicine?.qtyDelivered ?? "-"} unds.</Table.Td>
                <Table.Td ta={"center"}>{medicine?.qtyPending ?? "-"} unds.</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Box>
    );
  };

  const renderCard = (option: (typeof ordersList.results)[number], idx: number) => {
    const date = (option?.createdAtMedicalOrders ?? "").split(" | ")[0].replaceAll("/", "-");
    return (
      <Card
        key={option?.orderId}
        mb={"md"}
        className="pointer"
        data-testid={`insurancelist-card-${idx}`}
        p={0}
        onClick={() => handleCard(option)}
      >
        <Box py={"sm"} px={"lg"} data-testid={`insurancelist-card-content-${idx}`}>
          <Box
            py={"xs"}
            className="flex justify-between"
            data-testid={`insurancelist-card-header-${idx}`}
          >
            <Flex justify={"space-between"} align={"center"} flex={1}>
              <Title size={"h5"} py={0} className="secondary-color">
                <b>
                  Fórmula de medicamentos -{" "}
                  {medicalOrderTypeLabels[option.medicalOrderType] ?? option.medicalOrderType}
                </b>
              </Title>
              <IconChevronRight
                style={{ width: 24, height: 24 }}
                data-testid={`insurancelist-card-action-${idx}`}
              />
            </Flex>
          </Box>
          <Grid py={"0.2rem"}>
            <Grid.Col span={3}>
              <b>Sede</b>
            </Grid.Col>
            <Grid.Col span={9}>{option?.site}</Grid.Col>
          </Grid>
          <Grid py={"0.2rem"}>
            <Grid.Col span={3}>
              <b>Médico</b>
            </Grid.Col>
            <Grid.Col span={9}>{option?.doctor}</Grid.Col>
          </Grid>
          <Grid py={"0.2rem"}>
            <Grid.Col span={3}>
              <b>Paciente</b>
            </Grid.Col>
            <Grid.Col span={9}>{option?.patient?.name}</Grid.Col>
          </Grid>
          <Grid py={"0.2rem"}>
            <Grid.Col span={3}>
              <b>Identificación</b>
            </Grid.Col>
            <Grid.Col span={9}>
              {option?.patient?.documentType} {option?.patient?.document}
            </Grid.Col>
          </Grid>
          <Grid py={"0.2rem"}>
            <Grid.Col span={3}>
              <b>Fecha de orden</b>
            </Grid.Col>
            <Grid.Col span={9}>{date}</Grid.Col>
          </Grid>
          <Grid py={"0.2rem"}>
            <Grid.Col span={3}>
              <b>Fecha validez</b>
            </Grid.Col>
            <Grid.Col span={9}>{option?.validUntil}</Grid.Col>
          </Grid>
          {renderMedicinesSummary(option.medicines ?? [], idx)}
        </Box>
      </Card>
    );
  };

  const renderComponent = () => {
    return (
      <Fragment>
        {FiltersHeader()}
        <Box w={"100%"} data-testid="insurancelist-cards-container">
          {ordersList?.results?.length ? (
            ordersList?.results?.map((order, idx) => renderCard(order, idx))
          ) : (
            <Box data-testid="insurancelist-empty-state">No hay información para mostrar.</Box>
          )}
        </Box>
        <AppPagination
          total={ordersList?.rowTotal || 0}
          value={filters.page || 1}
          onChange={(value) => setFilters((state) => ({ ...state, page: value }))}
        />
      </Fragment>
    );
  };

  return <div className="main-container">{renderComponent()}</div>;
};

export default InsuranceList;
